// /src/pages/ImovelDetalhesPage.jsx
// VERSÃO 9.0 SUPREMA - IMERSÃO MONUMENTAL & 7 ESTAÇÕES SYNC

import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
    FiArrowLeft, FiMapPin, FiMaximize2, FiHome, 
    FiCheckCircle, FiShare2, FiShield, FiZap,
    FiChevronLeft, FiChevronRight, FiGrid, FiStar, FiDroplet, FiLayout, FiPhone, FiX, FiCamera, FiDollarSign, FiBox
} from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import apiClient from '../api/apiClient';

const resolveImagePath = (path) => {
    if (!path) return 'https://via.placeholder.com/1920x1080?text=AGUARDANDO+MIDIA+GOLD';
    if (path.startsWith('http')) return path;
    const formattedPath = path.startsWith('/') ? path : `/${path}`;
    return `https://mos-imoveis.masterofsites.com.br${formattedPath}`;
};

// ==========================================================
// 🌌 MODAL DE IMERSÃO SUPREMA (TEATRO DE MÍDIA)
// ==========================================================
const ImovelImersaoModal = ({ imovel, categorias, activeCategory, setActiveCategory, activeIdx, setActiveIdx, onClose }) => {
    const currentGallery = categorias[activeCategory].length > 0 ? categorias[activeCategory] : categorias.todos;
    
    const nextImg = () => setActiveIdx((prev) => (prev + 1) % currentGallery.length);
    const prevImg = () => setActiveIdx((prev) => (prev === 0 ? currentGallery.length - 1 : prev - 1));

    return (
        <motion.div 
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[10000] bg-black/98 backdrop-blur-3xl flex flex-col items-center justify-center p-4 md:p-10"
        >
            {/* HUD SUPERIOR DO MODAL */}
            <div className="absolute top-8 left-8 right-8 flex justify-between items-center z-[10010]">
                <div className="flex items-center gap-6">
                    <div className="w-12 h-12 rounded-2xl border border-white/10 bg-white/5 flex items-center justify-center text-accent">
                        <FiCamera size={24} />
                    </div>
                    <div className="hidden md:block">
                        <h4 className="text-[11px] font-black uppercase tracking-[0.4em] text-white">Visualização de Ativo</h4>
                        <p className="text-[8px] font-bold text-white/30 uppercase tracking-widest mt-1">Ref: {imovel.codigoImovel}</p>
                    </div>
                </div>

                <div className="flex bg-white/5 backdrop-blur-xl border border-white/10 p-1 rounded-2xl max-w-[70%] overflow-x-auto hide-scrollbar">
                    {['todos', 'fachada', 'quartos', 'banheiros', 'social'].map(cat => (categorias[cat]?.length > 0 || cat === 'todos') && (
                        <button key={cat} onClick={() => { setActiveCategory(cat); setActiveIdx(0); }}
                            className={`flex items-center gap-2 px-6 py-3 rounded-xl text-[9px] font-black uppercase transition-all whitespace-nowrap
                                ${activeCategory === cat ? 'text-black shadow-xl' : 'text-white/30 hover:text-white'}`}
                            style={activeCategory === cat ? { backgroundColor: 'var(--accent-color)' } : {}}
                        >
                            <span>{cat}</span>
                        </button>
                    ))}
                </div>

                <button onClick={onClose} className="p-4 bg-white/5 border border-white/10 rounded-full text-white hover:bg-red-600 transition-all hover:rotate-90">
                    <FiX size={28} />
                </button>
            </div>

            {/* DISPLAY DE IMAGEM FULL (WIDESCREEN OPTIMIZED) */}
            <div className="relative w-full h-full flex items-center justify-center">
                <AnimatePresence mode="wait">
                    <motion.img 
                        key={activeIdx + activeCategory}
                        initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 1.02 }}
                        transition={{ duration: 0.5, ease: "easeOut" }}
                        src={resolveImagePath(currentGallery[activeIdx])} 
                        className="max-w-full max-h-full object-contain shadow-[0_0_100px_rgba(0,0,0,0.8)] rounded-lg"
                    />
                </AnimatePresence>

                <button onClick={prevImg} className="absolute left-0 p-10 text-white/10 hover:text-accent transition-all active:scale-75"><FiChevronLeft size={60} /></button>
                <button onClick={nextImg} className="absolute right-0 p-10 text-white/10 hover:text-accent transition-all active:scale-75"><FiChevronRight size={60} /></button>
            </div>

            {/* DOCK DE NAVEGAÇÃO RÁPIDA */}
            <div className="absolute bottom-8 flex gap-3 p-3 bg-white/5 backdrop-blur-2xl rounded-2xl border border-white/10 max-w-[90%] overflow-x-auto no-scrollbar">
                {currentGallery.map((img, idx) => (
                    <button key={idx} onClick={() => setActiveIdx(idx)}
                            className={`relative w-20 h-14 rounded-lg overflow-hidden border-2 transition-all flex-shrink-0 ${activeIdx === idx ? 'scale-110 shadow-2xl' : 'opacity-30 hover:opacity-100'}`}
                            style={{ borderColor: activeIdx === idx ? 'var(--accent-color)' : 'transparent' }}>
                        <img src={resolveImagePath(img)} className="w-full h-full object-cover" />
                    </button>
                ))}
            </div>
        </motion.div>
    );
};

// ==========================================================
// 🏛️ PÁGINA PRINCIPAL
// ==========================================================
export default function ImovelDetalhesPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [imovel, setImovel] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isImersaoOpen, setIsImersaoOpen] = useState(false);
    const [activeCategory, setActiveCategory] = useState('todos');
    const [activeIdx, setActiveIdx] = useState(0);

    useEffect(() => {
        const fetchImovel = async () => {
            try {
                const res = await apiClient.get(`/api/filtros/busca/${id}`);
                setImovel(res.data);
            } catch (err) { console.error("💥 Falha no uplink do ativo."); }
            finally { setLoading(false); }
        };
        fetchImovel();
    }, [id]);

    const categorias = useMemo(() => {
        if (!imovel) return {};
        return {
            todos: [...(imovel.imagensDestaque || []), ...(imovel.imagensQuartos || []), ...(imovel.imagensBanheiros || []), ...(imovel.imagensOutros || [])],
            fachada: imovel.imagensDestaque || [],
            quartos: imovel.imagensQuartos || [],
            banheiros: imovel.imagensBanheiros || [],
            social: imovel.imagensOutros || []
        };
    }, [imovel]);

    const currentGallery = useMemo(() => {
        if (!categorias[activeCategory]) return [];
        return categorias[activeCategory].length > 0 ? categorias[activeCategory] : categorias.todos;
    }, [categorias, activeCategory]);

    const formatCurrency = (val) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(val || 0);

    if (loading) return (
        <div className="h-screen flex flex-col items-center justify-center transition-all" style={{ backgroundColor: 'var(--bg-main)' }}>
            <FiZap className="animate-bounce text-accent mb-6" size={50} />
            <p className="text-[10px] font-black uppercase tracking-[0.6em] text-auto opacity-20">Descriptografando Ativo...</p>
        </div>
    );
    if (!imovel) return null;

    return (
        <div className="min-h-screen transition-all duration-700 selection:bg-accent selection:text-black"
             style={{ backgroundColor: 'var(--bg-main)' }}>
            
            {/* 🛰️ NAV SUPERIOR (HUD) */}
            <nav className="fixed top-0 w-full z-[100] backdrop-blur-3xl border-b px-8 md:px-16 py-6 flex justify-between items-center transition-all shadow-2xl"
                 style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-color)' }}>
                <button onClick={() => navigate(-1)} className="flex items-center gap-4 group text-auto">
                    <div className="w-10 h-10 rounded-full border border-auto flex items-center justify-center group-hover:border-accent transition-all">
                        <FiArrowLeft className="group-hover:-translate-x-1 transition-transform text-accent" />
                    </div>
                    <span className="text-[11px] font-black uppercase tracking-[0.3em] opacity-50 group-hover:opacity-100 transition-opacity">Voltar ao Radar</span>
                </button>
                
                <div className="flex items-center gap-8">
                    <button className="hidden md:flex items-center gap-3 text-[10px] font-black uppercase tracking-[0.3em] opacity-40 hover:opacity-100 transition-all text-auto">
                        <FiShare2 size={16} /> Compartilhar
                    </button>
                    <button className="px-10 py-3.5 rounded-[1.5rem] font-black text-[11px] uppercase tracking-[0.4em] shadow-2xl text-black transition-all hover:scale-105 active:scale-95"
                            style={{ backgroundColor: 'var(--accent-color)' }}>
                        Reservar Ativo
                    </button>
                </div>
            </nav>

            <main className="pt-32 pb-40">
                {/* 🖼️ SEÇÃO 01: GALERIA MONUMENTAL */}
                <section className="px-6 md:px-16">
                    <div className="max-w-[1600px] mx-auto relative h-[70vh] md:h-[80vh] rounded-[3.5rem] md:rounded-[4.5rem] overflow-hidden shadow-[0_50px_100px_rgba(0,0,0,0.4)] border transition-all bg-black group"
                         style={{ borderColor: 'var(--border-color)' }}>
                        
                        <div className="w-full h-full cursor-zoom-in relative" onClick={() => setIsImersaoOpen(true)}>
                            <AnimatePresence mode="wait">
                                <motion.img 
                                    key={activeIdx + activeCategory}
                                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                                    src={resolveImagePath(currentGallery[activeIdx])} 
                                    className="w-full h-full object-cover transition-transform duration-[4000ms] group-hover:scale-110 opacity-90 group-hover:opacity-100"
                                />
                            </AnimatePresence>
                            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                        </div>

                        {/* Badges Flutuantes */}
                        <div className="absolute bottom-12 left-12 flex gap-4 pointer-events-none">
                            <div className="backdrop-blur-xl px-6 py-3 rounded-2xl text-[10px] font-black uppercase tracking-widest border border-white/10 text-white bg-black/40 shadow-2xl">
                                REF: {imovel.codigoImovel}
                            </div>
                            <div className="backdrop-blur-xl px-6 py-3 rounded-2xl text-[10px] font-black uppercase tracking-widest border border-accent/20 text-accent bg-accent/5">
                                Ativo Verificado Gold
                            </div>
                        </div>

                        {/* Botão de Zoom Central */}
                        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-500 pointer-events-none">
                            <div className="p-8 bg-white/10 backdrop-blur-xl rounded-full border border-white/20 text-white shadow-2xl">
                                <FiMaximize2 size={40} className="animate-pulse" />
                            </div>
                        </div>
                    </div>
                </section>

                {/* 📝 SEÇÃO 02: DOSSIÊ TÁTICO */}
                <section className="max-w-[1600px] mx-auto px-8 md:px-16 mt-24 grid grid-cols-1 lg:grid-cols-12 gap-20">
                    
                    {/* COLUNA DE DADOS (8/12) */}
                    <div className="lg:col-span-8 space-y-20">
                        <div className="space-y-8">
                            <div className="flex items-center gap-4 text-accent">
                                <FiMapPin size={20} />
                                <span className="font-bold uppercase text-sm tracking-[0.3em] opacity-70 text-auto">{imovel.localizacao?.bairro} // {imovel.localizacao?.cidade}</span>
                            </div>
                            <h1 className="text-5xl md:text-8xl font-black uppercase tracking-tighter leading-[0.85] text-auto">
                                {imovel.titulo}
                            </h1>
                        </div>

                        {/* Grade de Specs Industrial */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 py-12 border-y border-auto">
                            <SpecItem icon={FiMaximize2} label="Área Útil" value={`${imovel.caracteristicas?.areaUtil}m²`} />
                            <SpecItem icon={FiHome} label="Configuração" value={`${imovel.caracteristicas?.quartos} Qts`} />
                            <SpecItem icon={FiStar} label="Suítes" value={`${imovel.caracteristicas?.suites} Suítes`} />
                            <SpecItem icon={FiBox} label="Vagas" value={`${imovel.caracteristicas?.vagasGaragem} Vagas`} />
                        </div>

                        <div className="space-y-10">
                            <div className="flex items-center gap-4">
                                <div className="h-px flex-1 bg-auto opacity-10" />
                                <h3 className="text-[11px] font-black uppercase tracking-[0.6em] text-auto opacity-30">Dossiê do Ativo</h3>
                                <div className="h-px flex-1 bg-auto opacity-10" />
                            </div>
                            <p className="leading-relaxed text-2xl font-medium tracking-tight text-auto opacity-70 whitespace-pre-line italic border-l-4 border-accent pl-12">
                                "{imovel.descricao}"
                            </p>
                        </div>
                    </div>

                    {/* COLUNA FINANCEIRA (STICKY) (4/12) */}
                    <div className="lg:col-span-4">
                        <div className="sticky top-40 border rounded-[3.5rem] p-12 shadow-[0_30px_60px_rgba(0,0,0,0.2)] transition-all"
                             style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-color)' }}>
                            <div className="flex justify-between items-center mb-8">
                                <span className="text-[10px] font-black uppercase tracking-[0.5em] text-auto opacity-30">Quota de Investimento</span>
                                <FiShield className="text-accent" size={20} />
                            </div>
                            
                            <div className="space-y-4 mb-12">
                                <h2 className="text-6xl font-black tracking-tighter text-auto" style={{ color: 'var(--text-primary)' }}>
                                    {formatCurrency(imovel.valores?.venda || imovel.valorVenda)}
                                </h2>
                                <div className="flex items-center gap-3 px-4 py-2 bg-main-auto rounded-xl border border-auto w-fit opacity-50">
                                    <FiZap size={14} className="text-accent" />
                                    <span className="text-[9px] font-black uppercase tracking-widest text-auto">Oportunidade Indexada</span>
                                </div>
                            </div>

                            <div className="space-y-4">
                                <button className="w-full py-7 rounded-[1.5rem] font-black uppercase text-xs tracking-[0.4em] shadow-2xl text-black transition-all hover:scale-[1.02] active:scale-95 flex items-center justify-center gap-4"
                                        style={{ backgroundColor: 'var(--accent-color)' }}>
                                    <FiCheckCircle size={20} /> Solicitar Visita
                                </button>
                                <p className="text-center text-[8px] font-black uppercase tracking-widest text-auto opacity-20">
                                    Sujeito a aprovação de perfil comercial
                                </p>
                            </div>
                        </div>
                    </div>
                </section>
            </main>

            {/* 🌌 MODAL DE IMERSÃO */}
            <AnimatePresence>
                {isImersaoOpen && (
                    <ImovelImersaoModal 
                        imovel={imovel} 
                        categorias={categorias}
                        activeCategory={activeCategory}
                        setActiveCategory={setActiveCategory}
                        activeIdx={activeIdx}
                        setActiveIdx={setActiveIdx}
                        onClose={() => setIsImersaoOpen(false)}
                    />
                )}
            </AnimatePresence>
        </div>
    );
}

// 💎 SUB-COMPONENTE: ATRIBUTO TÁTICO
const SpecItem = ({ icon: Icon, label, value }) => (
    <div className="flex flex-col gap-3 group">
        <div className="flex items-center gap-3">
            <Icon size={14} className="text-accent opacity-50 group-hover:opacity-100 transition-opacity" />
            <span className="text-[9px] font-black uppercase tracking-[0.3em] text-auto opacity-30">{label}</span>
        </div>
        <span className="text-2xl font-black uppercase tracking-tighter text-auto group-hover:text-accent transition-colors">{value}</span>
    </div>
);